/**
 * Mortgage Calculator
 * Calculations for Spitzer amortization, annual schedules, and sensitivity analysis
 */

export interface AnnualRow {
  yearIndex: number;
  startBalance: number;
  principalPaidYear: number;
  speedPct: number;
  monthlyPayment: number;
  shockMonthlyDelta: number;
  sensitivityPctOfPayment: number;
  endBalance: number;
  interestPaidYear: number;
  monthsSimulated: number;
  totalPaidYear: number;
  costPer100: number | null;
  hasRateUpdate?: boolean; // For VARIABLE tracks: indicates if this year has a scheduled rate update
}

export interface AmortizeYearResult {
  startBalance: number;
  endBalance: number;
  principalPaidYear: number;
  interestPaidYear: number;
  monthsSimulated: number;
  monthlyPaymentUsed: number;
}

/**
 * Calculate monthly payment for Spitzer (equal monthly payments)
 * @param principal - Loan principal amount
 * @param annualRatePct - Annual interest rate as percentage (e.g., 4.6)
 * @param months - Number of months
 * @returns Monthly payment amount
 */
export function pmtSpitzer(
  principal: number,
  annualRatePct: number,
  months: number
): number {
  if (principal <= 0 || months <= 0) return 0;

  const r = annualRatePct / 100 / 12;

  // Edge case: zero interest
  if (r === 0) {
    return principal / months;
  }

  // Standard PMT formula: P * r / (1 - (1+r)^(-n))
  const pmt = (principal * r) / (1 - Math.pow(1 + r, -months));
  return pmt;
}

/**
 * Calculate monthly payment for Spitzer mortgage (alias for pmtSpitzer)
 * @param principal - Loan principal amount
 * @param annualRatePct - Annual interest rate as percentage (e.g., 4.6)
 * @param months - Number of months
 * @returns Monthly payment amount
 */
export function calculateMonthlyPayment(
  principal: number,
  annualRatePct: number,
  months: number
): number {
  return pmtSpitzer(principal, annualRatePct, months);
}

/**
 * Validate Spitzer mortgage input parameters
 * @param principal - Loan principal amount
 * @param annualRatePct - Annual interest rate as percentage
 * @param monthlyPayment - Desired monthly payment
 * @returns Validation result with reason if invalid
 */
export function validateSpitzerInput(
  principal: number,
  annualRatePct: number,
  monthlyPayment: number
): { valid: boolean; reason?: string } {
  if (principal <= 0) {
    return { valid: false, reason: 'Principal must be positive' };
  }

  if (monthlyPayment <= 0) {
    return { valid: false, reason: 'Monthly payment must be positive' };
  }

  const r = annualRatePct / 100 / 12;
  const minPayment = principal * r;

  if (monthlyPayment <= minPayment) {
    return { valid: false, reason: 'Monthly payment too low - would cause negative amortization' };
  }

  return { valid: true };
}

/**
 * Calculate remaining balance after a certain number of months
 * @param principal - Original loan principal
 * @param annualRatePct - Annual interest rate as percentage
 * @param monthsTotal - Total number of months for the loan
 * @param monthsElapsed - Number of months already elapsed
 * @returns Remaining balance
 */
export function calculateRemainingBalance(
  principal: number,
  annualRatePct: number,
  monthsTotal: number,
  monthsElapsed: number
): number {
  if (principal <= 0 || monthsTotal <= 0 || monthsElapsed < 0) return 0;
  if (monthsElapsed >= monthsTotal) return 0;

  const r = annualRatePct / 100 / 12;

  // Edge case: zero interest
  if (r === 0) {
    return principal * (1 - monthsElapsed / monthsTotal);
  }

  // Remaining balance formula: B = P * ((1+r)^n - (1+r)^p) / ((1+r)^n - 1)
  const numerator = Math.pow(1 + r, monthsTotal) - Math.pow(1 + r, monthsElapsed);
  const denominator = Math.pow(1 + r, monthsTotal) - 1;

  return principal * (numerator / denominator);
}

/**
 * Simulate one year of amortization month by month
 * @param principal - Starting balance
 * @param annualRatePct - Annual interest rate as percentage
 * @param monthsRemaining - Total months remaining on the loan
 * @param monthlyPayment - Optional: provided monthly payment, will compute if not given
 * @returns Amortization result for the year
 */
export function amortizeYear(
  principal: number,
  annualRatePct: number,
  monthsRemaining: number,
  monthlyPayment?: number
): AmortizeYearResult {
  const startBalance = principal;
  let balance = principal;
  let principalPaidYear = 0;
  let interestPaidYear = 0;
  let monthsSimulated = 0;

  // Determine monthly payment to use
  let pmt = monthlyPayment && monthlyPayment > 0
    ? monthlyPayment
    : pmtSpitzer(principal, annualRatePct, monthsRemaining);

  const r = annualRatePct / 100 / 12;

  // Simulate up to 12 months or until loan ends
  const monthsToSimulate = Math.min(12, monthsRemaining);

  for (let m = 0; m < monthsToSimulate; m++) {
    if (balance <= 0) break;

    const interestThisMonth = balance * r;
    const principalThisMonth = Math.min(pmt - interestThisMonth, balance);

    // Validate that payment covers interest
    if (principalThisMonth < 0) {
      // Payment too low, negative amortization would occur
      // Fallback to computed PMT
      pmt = pmtSpitzer(balance, annualRatePct, monthsRemaining - m);
      const newInterest = balance * r;
      const newPrincipal = Math.min(pmt - newInterest, balance);

      interestPaidYear += newInterest;
      principalPaidYear += newPrincipal;
      balance -= newPrincipal;
    } else {
      interestPaidYear += interestThisMonth;
      principalPaidYear += principalThisMonth;
      balance -= principalThisMonth;
    }

    monthsSimulated++;
  }

  return {
    startBalance,
    endBalance: Math.max(0, balance),
    principalPaidYear,
    interestPaidYear,
    monthsSimulated,
    monthlyPaymentUsed: pmt,
  };
}

/**
 * Derive months remaining from track data
 * @param track - Mortgage track object
 * @param reportDate - Report date string (ISO format YYYY-MM-DD)
 * @returns Number of months remaining or null if cannot derive
 */
export function deriveMonthsRemaining(
  track: {
    months?: number | null;
    expected_end_date?: string | null;
  },
  reportDate?: string | null
): number | null {
  // Prefer track.months
  if (track.months && track.months > 0) {
    return track.months;
  }

  // Try to derive from dates
  if (reportDate && track.expected_end_date) {
    try {
      const report = new Date(reportDate);
      const end = new Date(track.expected_end_date);

      if (end <= report) return null; // Already ended

      // Calculate month difference
      let months = (end.getFullYear() - report.getFullYear()) * 12;
      months += end.getMonth() - report.getMonth();

      // Adjust for day of month
      if (end.getDate() < report.getDate()) {
        months--;
      }

      return Math.max(1, Math.ceil(months));
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Calculate months remaining from monthly payment (inverse PMT formula)
 * Uses binary search to find the number of months
 * @param principal - Loan balance
 * @param annualRatePct - Annual interest rate as percentage
 * @param monthlyPayment - Desired monthly payment
 * @returns Number of months, or null if no solution exists
 */
export function calculateMonthsFromPayment(
  principal: number,
  annualRatePct: number,
  monthlyPayment: number
): number | null {
  if (principal <= 0 || monthlyPayment <= 0) return null;

  const r = annualRatePct / 100 / 12;

  // Check if payment is at least covering interest
  const minPayment = principal * r;
  if (monthlyPayment < minPayment) return null;

  // Edge case: zero interest
  if (r === 0) {
    return Math.ceil(principal / monthlyPayment);
  }

  // Binary search for months
  let low = 1;
  let high = 600; // Max 50 years

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const computedPmt = pmtSpitzer(principal, annualRatePct, mid);

    if (Math.abs(computedPmt - monthlyPayment) < 0.01) {
      return mid;
    }

    if (computedPmt > monthlyPayment) {
      // Need more months (lower payment)
      low = mid + 1;
    } else {
      // Need fewer months (higher payment)
      high = mid - 1;
    }
  }

  // Return closest match
  return high > 0 ? high : null;
}

/**
 * Calculate number of months between two dates
 * @param startDate - Start date (ISO string YYYY-MM-DD)
 * @param endDate - End date (ISO string YYYY-MM-DD)
 * @returns Number of months between dates, or null if invalid
 */
export function monthsBetweenDates(
  startDate: string,
  endDate: string
): number | null {
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end <= start) return null;

    let months = (end.getFullYear() - start.getFullYear()) * 12;
    months += end.getMonth() - start.getMonth();

    // Adjust for day of month
    if (end.getDate() < start.getDate()) {
      months--;
    }

    return Math.max(0, months);
  } catch {
    return null;
  }
}

/**
 * Project balance forward by simulating N months of amortization
 * @param currentBalance - Starting balance
 * @param annualRatePct - Annual interest rate as percentage
 * @param monthsRemaining - Total months remaining on loan
 * @param monthsToProject - Number of months to project forward
 * @param monthlyPayment - Optional monthly payment (will compute if not provided)
 * @returns Projected balance after N months
 */
export function projectBalanceToMonths(
  currentBalance: number,
  annualRatePct: number,
  monthsRemaining: number,
  monthsToProject: number,
  monthlyPayment?: number
): number {
  if (currentBalance <= 0 || monthsToProject <= 0) {
    return currentBalance;
  }

  let balance = currentBalance;
  const r = annualRatePct / 100 / 12;

  // Determine monthly payment
  let pmt = monthlyPayment && monthlyPayment > 0
    ? monthlyPayment
    : pmtSpitzer(currentBalance, annualRatePct, monthsRemaining);

  // Simulate month by month
  for (let m = 0; m < monthsToProject; m++) {
    if (balance <= 0) break;

    const interestThisMonth = balance * r;
    const principalThisMonth = Math.min(pmt - interestThisMonth, balance);

    if (principalThisMonth < 0) {
      // Payment too low, recalculate
      pmt = pmtSpitzer(balance, annualRatePct, monthsRemaining - m);
      const newInterest = balance * r;
      const newPrincipal = Math.min(pmt - newInterest, balance);
      balance -= newPrincipal;
    } else {
      balance -= principalThisMonth;
    }
  }

  return Math.max(0, balance);
}

/**
 * Compute shock delta using Spitzer formula comparison
 * @param balance - Current balance
 * @param annualRatePct - Current annual interest rate as percentage
 * @param monthsRemaining - Months remaining on loan
 * @returns Difference in monthly payment from +1% rate shock
 */
export function computeShockDeltaSpitzer(
  balance: number,
  annualRatePct: number,
  monthsRemaining: number
): number {
  if (balance <= 0 || monthsRemaining <= 0) return 0;

  const pmtBase = pmtSpitzer(balance, annualRatePct, monthsRemaining);
  const pmtShock = pmtSpitzer(balance, annualRatePct + 1.0, monthsRemaining);

  return pmtShock - pmtBase;
}

/**
 * Build annual amortization schedule with sensitivity analysis
 * @param params - Track parameters
 * @returns Array of annual rows with calculations
 */
export function buildAnnualSchedule(params: {
  currentBalance: number;
  annualRatePct: number;
  monthsRemaining: number;
  monthlyPayment?: number | null;
  trackType?: 'FIXED' | 'PRIME' | 'VARIABLE' | string;
  interestUpdateMonths?: number | null;
  nextInterestChangeDate?: string | null;
  reportDate?: string | null;
}): AnnualRow[] {
  const {
    currentBalance,
    annualRatePct,
    monthsRemaining,
    monthlyPayment,
    trackType,
    interestUpdateMonths,
    nextInterestChangeDate,
    reportDate
  } = params;

  if (currentBalance <= 0 || monthsRemaining <= 0) {
    return [];
  }

  const rows: AnnualRow[] = [];
  let balance = currentBalance;
  let remainingMonths = monthsRemaining;
  let yearIndex = 1;

  // For VARIABLE tracks: track period-based sensitivity
  let variablePeriodSensitivity: { shockDelta: number; sensitivityPct: number } | null = null;
  let variablePeriodStartYear = 1;

  while (remainingMonths > 0 && balance > 0) {
    // Simulate this year
    const yearResult = amortizeYear(
      balance,
      annualRatePct,
      remainingMonths,
      monthlyPayment || undefined
    );

    // Calculate speed (principal velocity)
    const speedPct = yearResult.startBalance > 0
      ? (yearResult.principalPaidYear / yearResult.startBalance) * 100
      : 0;

    // Calculate sensitivity to +1% shock (track-type aware)
    let shockMonthlyDelta = 0;
    let sensitivityPctOfPayment = 0;

    // Determine if this year has sensitivity exposure
    if (trackType === 'FIXED') {
      // FIXED tracks: no rate exposure
      shockMonthlyDelta = 0;
      sensitivityPctOfPayment = 0;
    } else if (trackType === 'PRIME') {
      // PRIME tracks: immediate exposure every year
      shockMonthlyDelta = computeShockDeltaSpitzer(
        yearResult.startBalance,
        annualRatePct,
        remainingMonths
      );
      sensitivityPctOfPayment = yearResult.monthlyPaymentUsed > 0
        ? (shockMonthlyDelta / yearResult.monthlyPaymentUsed) * 100
        : 0;
    } else if (trackType === 'VARIABLE') {
      // VARIABLE tracks: sensitivity is constant for entire period until next update
      // All years in a period show the SAME sensitivity (calculated from period start)

      let hasExposure = false;

      // Determine if THIS year has a rate update
      if (nextInterestChangeDate && reportDate) {
        const monthsUntilFirstChange = monthsBetweenDates(reportDate, nextInterestChangeDate);

        if (monthsUntilFirstChange !== null && interestUpdateMonths && interestUpdateMonths > 0) {
          const yearStartMonth = (yearIndex - 1) * 12;
          const yearEndMonth = yearIndex * 12;

          // Check if an update occurs in this year
          let checkMonth = monthsUntilFirstChange;
          while (checkMonth < yearEndMonth + 1200) {
            if (checkMonth >= yearStartMonth && checkMonth < yearEndMonth) {
              hasExposure = true;
              break;
            }
            if (checkMonth >= yearEndMonth) break;
            checkMonth += interestUpdateMonths;
          }
        } else if (monthsUntilFirstChange !== null) {
          const yearStartMonth = (yearIndex - 1) * 12;
          const yearEndMonth = yearIndex * 12;
          hasExposure = monthsUntilFirstChange >= yearStartMonth && monthsUntilFirstChange < yearEndMonth;
        }
      } else if (interestUpdateMonths && interestUpdateMonths > 0) {
        const yearStartMonth = (yearIndex - 1) * 12;
        const yearEndMonth = yearIndex * 12;
        const firstUpdateInYear = Math.ceil(yearStartMonth / interestUpdateMonths) * interestUpdateMonths;
        hasExposure = firstUpdateInYear < yearEndMonth;
      }

      // If this year has an update, it marks the END of the current period
      // We need to recalculate sensitivity for the NEW period starting next year
      // But for THIS year and all previous years in the period, use the SAME sensitivity

      if (hasExposure) {
        // This year has an update - it's the last year of the current period
        // Next year will start a new period
        variablePeriodStartYear = yearIndex + 1;
        variablePeriodSensitivity = null; // Reset for next period
      }

      // If we don't have cached sensitivity for this period, calculate it now
      if (variablePeriodSensitivity === null && yearIndex === variablePeriodStartYear) {
        // Calculate sensitivity based on the FIRST year's balance in this period
        const periodShockDelta = computeShockDeltaSpitzer(
          yearResult.startBalance,
          annualRatePct,
          remainingMonths
        );
        const periodSensitivityPct = yearResult.monthlyPaymentUsed > 0
          ? (periodShockDelta / yearResult.monthlyPaymentUsed) * 100
          : 0;

        variablePeriodSensitivity = {
          shockDelta: periodShockDelta,
          sensitivityPct: periodSensitivityPct,
        };
      }

      // Use the period's sensitivity for this year
      if (variablePeriodSensitivity !== null) {
        shockMonthlyDelta = variablePeriodSensitivity.shockDelta;
        sensitivityPctOfPayment = variablePeriodSensitivity.sensitivityPct;
      } else {
        // Shouldn't happen, but fallback
        shockMonthlyDelta = 0;
        sensitivityPctOfPayment = 0;
      }

      (yearResult as any).hasRateUpdate = hasExposure;
    } else {
      // Unknown track type: assume PRIME behavior (immediate exposure)
      shockMonthlyDelta = computeShockDeltaSpitzer(
        yearResult.startBalance,
        annualRatePct,
        remainingMonths
      );
      sensitivityPctOfPayment = yearResult.monthlyPaymentUsed > 0
        ? (shockMonthlyDelta / yearResult.monthlyPaymentUsed) * 100
        : 0;
    }

    // Calculate totalPaidYear and costPer100
    const totalPaidYear = yearResult.monthlyPaymentUsed * yearResult.monthsSimulated;
    const costPer100 = yearResult.principalPaidYear > 0
      ? (totalPaidYear / yearResult.principalPaidYear) * 100
      : null;

    rows.push({
      yearIndex,
      startBalance: yearResult.startBalance,
      principalPaidYear: yearResult.principalPaidYear,
      speedPct,
      monthlyPayment: yearResult.monthlyPaymentUsed,
      shockMonthlyDelta,
      sensitivityPctOfPayment,
      endBalance: yearResult.endBalance,
      interestPaidYear: yearResult.interestPaidYear,
      monthsSimulated: yearResult.monthsSimulated,
      totalPaidYear,
      costPer100,
      hasRateUpdate: (yearResult as any).hasRateUpdate,
    });

    // Update for next year
    balance = yearResult.endBalance;
    remainingMonths -= yearResult.monthsSimulated;
    yearIndex++;

    // Safety: stop if balance is essentially zero
    if (balance < 1) break;
  }

  return rows;
}
